/* @flow */

import * as React from 'react';
import ReactTooltip from 'react-tooltip';

import classnames from 'classnames';
import ReactDOMServer from 'react-dom/server';

import {colors} from '../colors';
import {HoverPopover} from '../hover-popover';
import {GoalLeadInfoIcon, WarningIcon, ErrorCircleIcon, ConfirmIcon, LockIcon} from '../icon';
import {Body, type TextSize} from '../typography';

import './status-badge.module.css';

export type Level =
    | 'info'
    | 'info--purple'
    | 'info--navy'
    | 'info--grey'
    | 'warning'
    | 'warning--grey'
    | 'alert'
    | 'alert--text'
    | 'success'
    | 'hidden';

export type StatusBadgeProps = {
    level: Level,
    text: string,
    detail?: string | React.Node,
    detailMaxWidth?: number,
    includeIcon?: boolean,
    customIcon?: any,
    isInteractive?: boolean,
    size?: TextSize,
    smallIcon?: boolean,
    badgeWidth?: string,
    onClick?: (event: SyntheticEvent<HTMLElement>) => void,
    getRef?: (ref: ?HTMLElement) => void,
};

type Props = {
    ...StatusBadgeProps,
};

export function StatusBadge(props: Props) {
    const {
        includeIcon,
        level,
        detail,
        text,
        size = 'normal',
        isInteractive,
        badgeWidth,
        customIcon,
        onClick,
    } = props;

    const statusBadgeRef = React.useRef(null);

    React.useEffect(() => {
        ReactTooltip.rebuild();
    }, []);

    const statusStyleName = classnames({
        'status-badge': true,
        'status-badge--interactive': isInteractive,
        'status-badge--clickable': onClick,
        'status-badge--warning': level === 'warning',
        'status-badge--alert': level === 'alert',
        'status-badge--alert-text': level === 'alert--text',
        'status-badge--info': level === 'info',
        'status-badge--success': level === 'success',
        'status-badge--hidden': level === 'hidden',
        'status-badge--info-purple': level === 'info--purple',
        'status-badge--info-navy': level === 'info--navy',
        'status-badge--info-grey': level === 'info--grey' || level === 'warning--grey',
    });

    let icon = null;

    if (includeIcon) {
        const Icon = getIcon(level);
        icon = (
            <Icon wrapWithDiv={false} size={props.smallIcon ? 12 : 15} fill={getFillColor(level)} />
        );
    }

    if (customIcon) {
        const Icon = customIcon;
        icon = <Icon wrapWithDiv={false} size={props.smallIcon ? 12 : 15} />;
    }

    let reactTooltipProps = {};
    let detailHoverPopover;
    if (detail) {
        switch (typeof detail) {
            case 'string':
                reactTooltipProps = {
                    ...(props.detailMaxWidth
                        ? {
                              'data-html': true,
                              'data-tip': ReactDOMServer.renderToString(
                                  <div style={{maxWidth: props.detailMaxWidth}}>{detail}</div>
                              ),
                          }
                        : {'data-tip': detail}),
                    'data-effect': 'solid',
                    'data-delay-show': '2',
                    'data-event': 'mouseenter focus',
                    'data-event-off': 'mouseleave blur click',
                };
                break;
            case 'object':
                detailHoverPopover = (
                    <HoverPopover
                        anchorRef={(ref) => {
                            statusBadgeRef.current = ref;
                            props.getRef && props.getRef(ref);
                        }}
                        location='top'
                        bodyColor='dark'
                        customStyles={{maxWidth: props.detailMaxWidth}}
                    >
                        {detail}
                    </HoverPopover>
                );
                break;
        }
    }

    const Component = onClick ? 'button' : 'div';

    return (
        <>
            <Component
                styleName={statusStyleName}
                tabIndex={onClick ? 0 : undefined}
                ref={(ref) => {
                    statusBadgeRef.current = ref;
                    props.getRef && props.getRef(ref);
                }}
                style={{width: badgeWidth}}
                onClick={
                    onClick
                        ? (e) => {
                              e.preventDefault();
                              if (onClick) {
                                  onClick(e);
                              }
                          }
                        : undefined
                }
                type={onClick ? 'button' : undefined}
                {...reactTooltipProps}
            >
                {icon}
                <Body size={size} color='inherit'>
                    {text}
                </Body>
            </Component>
            {detailHoverPopover}
        </>
    );
}

export function getFillColor(level: Level) {
    switch (level) {
        case 'info':
            return 'hsla(195, 71%, 37%, 1)';
        case 'warning--grey':
        case 'warning':
            return 'hsla(20, 81%, 56%, 1)';
        case 'alert':
        case 'alert--text':
            return 'hsla(360, 95%, 64%, 1)';
        case 'success':
            return 'hsla(145, 71%, 26%, 1)';
        case 'hidden':
            return colors.greyDk;
        default:
            return colors.grey;
    }
}

function getIcon(level: Level) {
    switch (level) {
        case 'info':
            return GoalLeadInfoIcon;
        case 'warning':
        case 'warning--grey':
            return WarningIcon;
        case 'alert':
        case 'alert--text':
            return ErrorCircleIcon;
        case 'success':
            return ConfirmIcon;
        case 'hidden':
            return LockIcon;
        default:
            return GoalLeadInfoIcon;
    }
}
